// Sign in with Microsoft (Oct 2026): the OpenID Connect code flow with PKCE,
// run in the browser against Acuity's own tenant — no library and no secret.
// The API (GET /auth/microsoft/config) says whether it is switched on and gives
// the app + tenant ids; the ID token we get back is checked by the API
// (POST /auth/microsoft), which also makes sure the address is an Acuity one.

const KEY = 'aflo_ms_login';

const b64url = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes)))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const random = (n) => b64url(crypto.getRandomValues(new Uint8Array(n)));

export const redirectUri = () => `${window.location.origin}/auth/microsoft`;

// Microsoft's messages start with a code ("AADSTS50020: User account … does not
// exist in tenant …") and run to several lines; keep the first, without the code.
const firstLine = (text) => (text || '').split(/\r?\n/)[0].replace(/^AADSTS\d+:\s*/, '').trim();

/** Off to Microsoft's sign-in page. `cfg` is /auth/microsoft/config. */
export async function startMicrosoftLogin(cfg) {
  const verifier = random(48);
  const challenge = b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
  const state = random(16);
  const nonce = random(16);
  sessionStorage.setItem(KEY, JSON.stringify({ verifier, state, nonce, tenant: cfg.tenant_id, client: cfg.client_id }));
  const params = new URLSearchParams({
    client_id: cfg.client_id,
    response_type: 'code',
    response_mode: 'query',
    redirect_uri: redirectUri(),
    scope: 'openid profile email',
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    prompt: 'select_account',
  });
  if (cfg.domain_hint) params.set('domain_hint', cfg.domain_hint);
  window.location.assign(`https://login.microsoftonline.com/${cfg.tenant_id}/oauth2/v2.0/authorize?${params}`);
}

/** Back from Microsoft (/auth/microsoft?code=…): { id_token, nonce } for the API. */
export async function finishMicrosoftLogin(search) {
  const q = new URLSearchParams(search);
  let saved = null;
  try { saved = JSON.parse(sessionStorage.getItem(KEY) || 'null'); } catch (_) { saved = null; }
  sessionStorage.removeItem(KEY);
  if (q.get('error')) {
    throw new Error(q.get('error') === 'access_denied' ? 'Microsoft sign-in was cancelled' : (firstLine(q.get('error_description')) || 'Microsoft sign-in failed'));
  }
  if (!saved || !q.get('code') || q.get('state') !== saved.state) {
    throw new Error('Microsoft sign-in expired — please try again');
  }
  const res = await fetch(`https://login.microsoftonline.com/${saved.tenant}/oauth2/v2.0/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      client_id: saved.client,
      grant_type: 'authorization_code',
      code: q.get('code'),
      redirect_uri: redirectUri(),
      code_verifier: saved.verifier,
      scope: 'openid profile email',
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.id_token) {
    throw new Error(firstLine(data.error_description) || 'Microsoft sign-in failed — please try again');
  }
  return { id_token: data.id_token, nonce: saved.nonce };
}
